import { useRef, useState } from 'react';
import { RecaptchaVerifier } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { auth } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

// Set to true once Firebase Blaze plan is enabled for phone auth
export const ENABLE_PHONE_AUTH = false;

/**
 * Phone-number sign-in flow (send OTP → verify OTP), shared between
 * LoginPage and RegisterPage — previously duplicated ~90-line blocks in
 * each. Self-contained: owns its own step/error/loading state rather than
 * sharing the parent form's, since the two are visually and functionally
 * independent once split out.
 */
export default function PhoneAuthSection({ onSuccess, disabled }) {
  const { t } = useTranslation();
  const { sendPhoneOTP, verifyPhoneOTP } = useAuth();

  const [phoneStep, setPhoneStep] = useState(null); // null | 'number' | 'otp'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const recaptchaContainer = useRef(null);
  const recaptchaVerifier = useRef(null);

  if (!ENABLE_PHONE_AUTH) return null;

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (!recaptchaVerifier.current) {
        recaptchaVerifier.current = new RecaptchaVerifier(auth, recaptchaContainer.current, { size: 'invisible' });
      }
      const result = await sendPhoneOTP(phone, recaptchaVerifier.current);
      if (!result.ok) {
        recaptchaVerifier.current = null;
        setError(result.message);
        setLoading(false);
        return;
      }
      setConfirmationResult(result.confirmationResult);
      setPhoneStep('otp');
    } catch {
      recaptchaVerifier.current = null;
      setError(t('auth.otpSendError'));
    }
    setLoading(false);
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await verifyPhoneOTP(confirmationResult, otp);
    setLoading(false);
    if (!result.ok) { setError(result.message); return; }
    onSuccess();
  };

  const resetPhone = () => {
    setPhoneStep(null);
    setPhone('');
    setOtp('');
    setConfirmationResult(null);
    recaptchaVerifier.current = null;
    setError('');
  };

  return (
    <>
      {!phoneStep && (
        <button
          type="button"
          className="social-btn"
          onClick={() => { setError(''); setPhoneStep('number'); }}
          disabled={disabled}
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          {t('auth.continueWithPhone')}
        </button>
      )}

      {phoneStep === 'number' && (
        <form onSubmit={handleSendOTP} className="w-full space-y-2">
          <div ref={recaptchaContainer} />
          <div className="form-row">
            <label htmlFor="phone">{t('auth.phoneNumber')}</label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="input"
              placeholder="+1 234 567 8900"
              autoComplete="tel"
              required
            />
          </div>
          {error && (
            <div className="error-box">
              <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {error}
            </div>
          )}
          <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
            {loading ? t('auth.sending') : t('auth.sendOTP')}
          </button>
          <button type="button" className="social-btn" onClick={resetPhone}>{t('auth.cancel')}</button>
        </form>
      )}

      {phoneStep === 'otp' && (
        <form onSubmit={handleVerifyOTP} className="w-full space-y-2">
          <p className="text-xs text-slate-500 text-center">{t('auth.codeSentTo')} {phone}</p>
          <div className="form-row">
            <label htmlFor="otp">{t('auth.verificationCode')}</label>
            <input
              id="otp"
              type="text"
              inputMode="numeric"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              className="input"
              placeholder={t('auth.verificationCode')}
              maxLength={6}
              required
            />
          </div>
          {error && (
            <div className="error-box">
              <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {error}
            </div>
          )}
          <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
            {loading ? t('auth.verifying') : t('auth.verifyCode')}
          </button>
          <button type="button" className="social-btn" onClick={() => { setOtp(''); setError(''); setPhoneStep('number'); }}>
            {t('auth.resendCode')}
          </button>
          <button type="button" className="social-btn" onClick={resetPhone}>{t('auth.cancel')}</button>
        </form>
      )}
    </>
  );
}
